import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
    Box,
    Button,
    Chip, IconButton,
    InputAdornment,
    TextField, Tooltip
} from '@mui/material';
import { Add, Edit, WarningAmber } from '@mui/icons-material';
import Currency from 'components/formaters/Currency';
import { CarModel, PriceTerms, StockModel } from 'models/StockModel';
import { PartyModel, PartyType } from 'models/PartyModel';
import { InvoiceLink } from 'modules/invoices/common/Components';
import usePartyModals from 'modules/parties/hooks/usePartyModals';
import auth from 'recoil/atoms/auth';


type PriceInputProps = {
    id: number,
    name: string,
    value?: number,
    onChange: (value: number, id: number, name: string) => void,
    currency: string,
    canEdit: boolean,
}

export const PriceInput = ({canEdit, currency, id, name, onChange, value}: PriceInputProps) => {
    if (!canEdit)
        return value ?
            <Currency value={value} currency={currency === '$' ? 'usd' : 'jpy'}/> :
            null;

    return (
        <TextField
            margin="none"
            value={value || ''}
            onChange={e => onChange(Number(e.target.value), id, name)}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">{currency}</InputAdornment>
                ),
            }}
            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
        />
    )
};


type PayerProps = {
    car: CarModel,
    main?: boolean,
    secondary?: number,
}

export const Payer = ({car, main, secondary}: PayerProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const {openAddPartyModal, openEditPartyModal} = usePartyModals();

    if (car.priceTerms === PriceTerms.FOB || secondary) {
        if (car.priceTerms === PriceTerms.FOB && !main)
            return null;

        const party = car.parties.consignee;

        const button = <ShowOrCreateParty
            type={PartyType.Consignee}
            party={party}
            partyable={car}
            partyableType="car"
            add={openAddPartyModal}
            edit={openEditPartyModal}
            can={my.permissions.has('parties create consignee')}
        />;

        if (party) {
            return <div>
                {secondary && <>, </>}
                {party.name}
                {' '}
                {button}
                {!party.address && <Box sx={{color: 'error.light'}}>{t('no_address')}</Box>}
            </div>
        }

        return <div>
            {secondary && <>, </>}
            <Box component="span" sx={{color: 'error.light'}}>{t('no_consignee')}</Box>
            {' '}
            {button}
        </div>
    }

    if (secondary === 0)
        return null;

    return <div>
        {t('shipper')}
    </div>;
};


type DealerProps = {
    car: CarModel,
}

export const Dealer = ({car}: DealerProps) => {
    const my = useRecoilValue(auth);
    const {openAddPartyModal, openEditPartyModal} = usePartyModals();

    const party = car.parties.dealer;

    return <>
        {party && (party.companyName || party.companyCode || party.lastName)}
        {' '}
        <ShowOrCreateParty
            type={PartyType.Dealer}
            party={party}
            partyable={car}
            partyableType="car"
            add={openAddPartyModal}
            edit={openEditPartyModal}
            can={my.permissions.has('parties create dealer')}
        />
    </>
};


type ShowOrCreatePartyProps = {
    party?: PartyModel,
    can: boolean,
    partyable: StockModel,
    add: (parent: string, parentId: number, type: PartyType, emails: string[]) => void,
    edit: (party: PartyModel) => void,
    partyableType: string,
    type: PartyType,
}

export const ShowOrCreateParty = ({can, party, partyable, add, edit, partyableType, type}: ShowOrCreatePartyProps) => {
    if (party)
        return (
            <IconButton
                className="show-on-hover"
                size="small"
                onClick={() => edit(party)}
            >
                <Edit fontSize="small"/>
            </IconButton>
        );

    if (!can) return null;

    return (
        <IconButton
            className="show-on-hover"
            size="small"
            onClick={() => add(partyableType, partyable.id, type, Object.values(partyable.parties).map(p => p?.email))}
        >
            <Add fontSize="small"/>
        </IconButton>
    )
};


type PriceFillerProps = {
    rate?: number,
    fillPrices: (price: number) => void,
}

export const PriceFillerInput = ({rate, fillPrices}: PriceFillerProps) => {
    const {t} = useTranslation();
    const [state, setState] = useState(rate);

    return (
        <TextField
            label={t('jpy_per_usd_rate')}
            value={state || ''}
            onChange={e => setState(Number(e.target.value))}
            margin="none"
            type="number"
            inputProps={{
                step: 0.01,
                min: 0,
            }}
            InputProps={{
                startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                endAdornment: (
                    <Button
                        sx={{width: 200}}
                        onClick={() => fillPrices(state!)}
                        disabled={!state}
                    >
                        {t('set_prices')}
                    </Button>
                )
            }}
        />
    )
};


type PriceWithInvoiceProps = {
    price: number,
    currency: string,
    paid: boolean,
    invoice: any,
}

export const PriceWithInvoice = ({currency, invoice, paid, price}: PriceWithInvoiceProps) => {
    const {t} = useTranslation();

    return (
        <Box textAlign="right">
            {!!invoice && !paid && (
                <Tooltip
                    title={t('not_paid')}
                    placement="top"
                >
                    <WarningAmber
                        color="error"
                        fontSize="small"
                        sx={{verticalAlign: 'middle'}}
                    />
                </Tooltip>
            )}
            {' '}
            {!!invoice && <InvoiceLink invoice={invoice}/>}
            <br/>
            <Currency
                value={price}
                currency={currency}
            />
        </Box>
    )
};


type PriceTermEditProps = {
    action: () => void,
}

export const PriceTermEdit = ({action}: PriceTermEditProps) => {
    return (
        <IconButton
            className="show-on-hover"
            size="small"
            onClick={action}
        >
            <Edit fontSize="small"/>
        </IconButton>
    );
}
